import { useEffect, useState } from 'react'
import { hasGivenConsentForGroups, OptanonConsentAnalyticsGroup } from '@/common/utils/cookies/hasGivenConsent'

interface UseHasGivenConsentOptions {
    /**
     * Read the current consent state synchronously on the first render instead of starting as `undefined`.
     * Prevents a flicker when the consent-gated UI would otherwise render its "not consented" state for one frame
     * (e.g. showing the placeholder before the video when consent was already given).
     */
    syncOnInit?: boolean
}

/**
 * Returns a reactive boolean that is true only when the user has consented to every one of the given OneTrust groups.
 * Updates whenever OneTrust consent changes (both granting and revoking), without requiring a page refresh.
 */
export const useHasGivenConsentForGroups = (
    groups: string[],
    { syncOnInit = false }: UseHasGivenConsentOptions = {}
) => {
    const groupsKey = groups.join(',')
    const [consent, setConsent] = useState<boolean | undefined>(() =>
        syncOnInit ? hasGivenConsentForGroups(groupsKey ? groupsKey.split(',') : []) : undefined
    )

    useEffect(() => {
        const requiredGroups = groupsKey ? groupsKey.split(',') : []

        const onetrustListener = (event: Event) => {
            const activeGroups = (event as CustomEvent).detail as string[] | undefined
            if (!Array.isArray(activeGroups)) {
                setConsent(hasGivenConsentForGroups(requiredGroups))
                return
            }
            setConsent(requiredGroups.every(group => activeGroups.includes(group)))
        }

        // Sync in case consent changed between render and effect.
        setConsent(hasGivenConsentForGroups(requiredGroups))

        window.addEventListener('OneTrustGroupsUpdated', onetrustListener)

        return () => {
            window.removeEventListener('OneTrustGroupsUpdated', onetrustListener)
        }
    }, [groupsKey])

    return consent
}

/**
 * Returns a reactive boolean that updates whenever OneTrust consent changes
 * (both granting and revoking), without requiring a page refresh.
 */
export const useHasGivenConsent = () => useHasGivenConsentForGroups([OptanonConsentAnalyticsGroup])
